/**
 * Tutorial progress, and the few things the lessons watch that no other store
 * keeps.
 *
 * Progress is per person and kept in this browser. Parallax has no per-user
 * settings store on the server (`/api/state` is shared by the whole
 * deployment, which is the opposite of what a personal record wants), so it
 * lives in localStorage under the username. Every read and write is wrapped:
 * a private window or a full quota must cost the trainee their tick marks,
 * never the screen.
 */

import { create } from 'zustand';

import { NEW_ZONE_TRACK, trackZones, type LessonId, type ZoneTrack } from './lessons';

const KEY_PREFIX = 'parallax.tutorial.v1.';

export interface ProgressStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): ProgressStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function loadProgress(username: string, storage = browserStorage()): LessonId[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(KEY_PREFIX + username);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is LessonId => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function saveProgress(username: string, done: readonly LessonId[], storage = browserStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(KEY_PREFIX + username, JSON.stringify(done));
  } catch {
    // Full or blocked. The ticks still show for this session.
  }
}

interface TutorialState {
  username: string | null;
  done: LessonId[];
  /** A pattern was generated since practice opened. */
  patternGenerated: boolean;
  zones: ZoneTrack;

  hydrate: (username: string) => void;
  complete: (ids: Iterable<LessonId>) => void;
  reset: () => void;
  notePattern: () => void;
  observeZones: (zoneIds: readonly string[]) => void;
  /** Forget what was observed in the last sandbox. Progress itself is kept. */
  beginSession: () => void;
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  username: null,
  done: [],
  patternGenerated: false,
  zones: NEW_ZONE_TRACK,

  hydrate: (username) => {
    if (get().username === username) return;
    set({ username, done: loadProgress(username) });
  },
  complete: (ids) => {
    const { done, username } = get();
    const next = [...done];
    for (const id of ids) if (!next.includes(id)) next.push(id);
    if (next.length === done.length) return;
    set({ done: next });
    if (username) saveProgress(username, next);
  },
  reset: () => {
    const { username } = get();
    set({ done: [], patternGenerated: false, zones: NEW_ZONE_TRACK });
    if (username) saveProgress(username, []);
  },
  notePattern: () => {
    if (!get().patternGenerated) set({ patternGenerated: true });
  },
  observeZones: (zoneIds) => {
    const current = get().zones;
    const next = trackZones(current, zoneIds);
    if (next !== current) set({ zones: next });
  },
  beginSession: () => set({ patternGenerated: false, zones: NEW_ZONE_TRACK }),
}));

/**
 * Called by the planning screen when it generates a search pattern. A pattern
 * is indistinguishable from hand-placed waypoints once it is in the draft, so
 * the moment of generating it is the only time it can be seen.
 */
export function noteTutorialPattern(): void {
  useTutorialStore.getState().notePattern();
}
